import './styles.css';

import { difficultyByLevel } from './ai/difficulty';
import type { TurnMove, WorkerResponse } from './ai/types';
import AIWorker from './ai/worker.ts?worker';
import { canUndo, popHistory, pushHistory } from './history';
import {
  type GameMode,
  type GameSettings,
  isAITurn,
  loadSettings,
  resolveHumanColor,
  saveSettings,
} from './mode';
import {
  animateTransition,
  buildUI,
  flashArrive,
  hideModeDialog,
  render,
  resetScoreTracking,
  setAIThinking,
  showModeDialog,
  toggleRulesPanel,
  type UI,
} from './render';
import {
  applyCellClick,
  endTurn,
  initialState,
  recoverIfMidAITurn,
  type Transition,
} from './state';
import { clearSavedGame, loadGame, saveGame } from './storage';
import { BLACK, type Color, type GameState, PHASE, WHITE } from './types';

let state: GameState = initialState();
let history: GameState[] = [];
let settings: GameSettings = loadSettings();
/** Concrete color the human plays (set when a game starts). null in 2P mode. */
let humanColor: Color | null = null;
/** True while an animation is in flight; suppresses user input. */
let busy = false;
/**
 * True from "AI starts thinking" through "AI's playback animations are done"
 * — covers the gap *between* sub-animations during a multi-jump so the
 * board UI never flashes move-option highlights while the computer is
 * acting. Distinct from `busy`, which only covers individual animations.
 */
let aiActive = false;
/** If non-null, the AI worker has failed and we surface this to the player. */
let aiError: string | null = null;
/** Wall-clock time the most recent AI turn started thinking. */
let aiThinkStart = 0;
/**
 * Minimum perceived "thinking" time for the AI. Even on Beginner, where the
 * search returns in <1 ms, we pad to this floor so the player has time to
 * register that the computer is taking its turn — without it, stones
 * teleport before the eye catches up.
 */
const MIN_AI_THINK_MS = 300;
let ui: UI;
let aiWorker: Worker | null = null;
let aiRequestId = 0;
/** Settings stored alongside the saved game so reload restores the AI context. */
const GAME_META_KEY = 'konane:v1:gameMeta';

interface GameMeta {
  mode: GameMode;
  humanColor: Color | null;
  difficulty: number;
}

function init(): void {
  const root = document.getElementById('app');
  if (!root) throw new Error('Missing #app element');

  ui = buildUI(root, {
    onCellClick: handleCellClick,
    onNewGame: handleNewGame,
    onUndo: handleUndo,
    onEndTurn: handleEndTurn,
    onToggleRules: () => toggleRulesPanel(ui),
    onPlayAgain: handlePlayAgain,
    onModeStart: handleModeStart,
    onModeCancel: handleModeCancel,
  });

  const loaded = loadGame();
  const meta = loadGameMeta();
  if (loaded && meta) {
    state = loaded.state;
    history = loaded.history;
    humanColor = meta.humanColor;
    settings = { ...settings, mode: meta.mode, humanColor: meta.humanColor ?? settings.humanColor };

    // Defense in depth: if the saved state is mid-turn (MULTI / CAPTURE) for
    // the AI's color — possible from a pre-fix save where intermediate AI
    // states were persisted — normalise it so the worker can resume cleanly.
    const aiColor =
      settings.mode === 'vs_computer' && humanColor !== null
        ? humanColor === BLACK
          ? WHITE
          : BLACK
        : null;
    const recovered = recoverIfMidAITurn(state, aiColor);
    if (recovered !== state) {
      state = recovered;
      saveGame(state, history);
    }
  } else {
    // No saved game; show mode dialog before play begins.
    showModeDialog(ui, settings, false);
  }

  resetScoreTracking(state);
  renderAll(true);

  if (loaded) {
    // If it's the AI's turn on a restored game (e.g., user closed the tab
    // while waiting), kick the AI immediately.
    maybeStartAITurn();
  }
}

function handleCellClick(r: number, c: number): void {
  if (busy || aiActive) return;
  if (state.phase === PHASE.OVER) return;
  if (isAITurn(settings.mode, humanColor, state.turn, false)) return;
  const t = applyCellClick(state, r, c);
  void applyTransition(t).then(() => maybeStartAITurn());
}

function handleEndTurn(): void {
  if (busy || aiActive) return;
  if (state.phase !== PHASE.MULTI) return;
  if (isAITurn(settings.mode, humanColor, state.turn, false)) return;
  const t = endTurn(state);
  void applyTransition(t).then(() => maybeStartAITurn());
}

function handleUndo(): void {
  if (busy || aiActive) return;
  // In vs-computer mode, undo two snapshots if possible so the human gets
  // back to their own turn (rather than staring at the AI's pending move).
  const stepsBack = settings.mode === 'vs_computer' && humanColor && history.length >= 2 ? 2 : 1;
  let h = history;
  let s = state;
  for (let i = 0; i < stepsBack; i++) {
    const popped = popHistory(h);
    if (!popped) break;
    h = popped.history;
    s = popped.state;
  }
  history = h;
  state = s;
  resetScoreTracking(state);
  persist();
  renderAll(true);
}

function handleNewGame(): void {
  // Allow New Game even during AI activity — kid's emergency exit hatch.
  // The dialog itself is blocked from changing state until the user picks
  // a mode, and `startNewGame` cleans up any in-flight AI work.
  if (busy) return;
  showModeDialog(ui, settings, !isFreshGame(state));
}

function handleModeStart(newSettings: GameSettings): void {
  settings = newSettings;
  saveSettings(settings);
  hideModeDialog(ui);
  startNewGame();
}

function handleModeCancel(): void {
  hideModeDialog(ui);
}

function handlePlayAgain(): void {
  // Modal "Play Again" → re-show mode dialog so the user can pick the
  // opponent for the next round.
  ui.modalOverlay.setAttribute('hidden', '');
  showModeDialog(ui, settings, false);
}

function startNewGame(): void {
  // Invalidate any worker response that may still be in flight for the
  // previous game, and clear all transient AI state so the new game starts
  // from a clean slate even if the old AI playback hasn't finished yet.
  aiRequestId++;
  aiActive = false;
  busy = false;
  aiError = null;
  setAIThinking(ui, false);

  state = initialState();
  history = [];
  clearSavedGame();
  humanColor = settings.mode === 'vs_computer' ? resolveHumanColor(settings) : null;
  saveGameMeta();
  resetScoreTracking(state);
  renderAll(true);
  maybeStartAITurn();
}

function isFreshGame(s: GameState): boolean {
  return s.phase === PHASE.SETUP_B && s.turnCount === 0;
}

async function applyTransition(t: Transition): Promise<void> {
  if (!t.changed && t.state === state) {
    renderAll(false);
    return;
  }

  if (t.moved || t.capturedAt) {
    busy = true;
    try {
      await animateTransition(ui, t);
    } finally {
      busy = false;
    }
  }

  if (t.changed) {
    history = pushHistory(history, state);
  }
  state = t.state;
  persist();
  renderAll(false);

  if (t.moved) {
    flashArrive(ui, t.moved.to.r, t.moved.to.c);
  }
}

/**
 * Write the current state + history to localStorage.
 *
 * **Suppressed while `aiActive` is true.** During AI playback (multi-jumps
 * especially) the state momentarily lives in PHASE.MULTI or PHASE.CAPTURE.
 * If a refresh landed in one of those phases for the AI's color, the
 * worker couldn't generate a move and the game would freeze. We defer the
 * write until the AI's turn finishes; see `maybeStartAITurn`.
 */
function persist(): void {
  if (aiActive) return;
  saveGame(state, history);
  saveGameMeta();
}

function renderAll(silent: boolean): void {
  render(ui, state, {
    canUndo: canUndo(history),
    silentScores: silent,
    mode: settings.mode,
    difficulty: settings.difficulty,
    humanColor,
    aiBusy: aiActive,
    aiError,
  });
}

// ────────────────────────────────────────────────────────────────────────
//  AI orchestration
// ────────────────────────────────────────────────────────────────────────

function maybeStartAITurn(): void {
  const isAITurnNow =
    state.phase !== PHASE.OVER &&
    settings.mode === 'vs_computer' &&
    humanColor !== null &&
    humanColor !== state.turn;
  if (!isAITurnNow) {
    // Control returns to the human. Release the AI-active flag, commit the
    // now-final state to storage (persist() was suppressed during the AI's
    // turn), and re-render so move-option highlights come back.
    if (aiActive) {
      aiActive = false;
      persist();
      renderAll(false);
    }
    return;
  }
  startAITurn();
}

function startAITurn(): void {
  if (busy) return;
  busy = true;
  aiActive = true;
  aiThinkStart = performance.now();
  setAIThinking(ui, true);
  renderAll(false);

  const id = ++aiRequestId;
  const opts = difficultyByLevel(settings.difficulty).searchOpts;
  ensureWorker().postMessage({ state, opts, id });
}

function ensureWorker(): Worker {
  if (aiWorker) return aiWorker;
  aiWorker = new AIWorker();
  aiWorker.onmessage = async (ev: MessageEvent<WorkerResponse>) => {
    if (ev.data.id !== aiRequestId) return; // stale
    // Even on Beginner the search can finish faster than the eye can track.
    // Pad to a minimum perceived think time so the user registers the AI
    // taking its turn instead of pieces teleporting.
    const elapsed = performance.now() - aiThinkStart;
    const remaining = MIN_AI_THINK_MS - elapsed;
    if (remaining > 0) await sleep(remaining);
    setAIThinking(ui, false);
    busy = false;
    if (!ev.data.move) {
      aiActive = false;
      renderAll(false);
      return;
    }
    void playAIMove(ev.data.move);
  };
  aiWorker.onerror = (err) => {
    console.error('AI worker error:', err);
    setAIThinking(ui, false);
    busy = false;
    aiActive = false;
    aiError =
      'The computer player failed to load. Click "New Game" and choose "Two players" to keep playing.';
    renderAll(false);
  };
  // If the worker fails to load entirely (CSP blocked, restricted browser),
  // the `error` event also fires. The handler above covers that path.
  return aiWorker;
}

async function playAIMove(move: TurnMove): Promise<void> {
  // Replay the AI's chosen turn as a sequence of clicks so existing animations
  // (piece-slide + capture-fade) play naturally. `aiActive` stays true the
  // whole time so move-option highlights remain suppressed between jumps.
  if (move.kind === 'setup_b' || move.kind === 'setup_w') {
    const t = applyCellClick(state, move.pos.r, move.pos.c);
    await applyTransition(t);
    maybeStartAITurn();
    return;
  }

  // jump move: click source, then each landing square, then End Turn if
  // continuations were available but the AI chose to stop.
  let t = applyCellClick(state, move.from.r, move.from.c);
  await applyTransition(t);
  for (const step of move.steps) {
    t = applyCellClick(state, step.tr, step.tc);
    await applyTransition(t);
    // small breath between consecutive jumps for visual clarity
    await sleep(120);
  }
  if (state.phase === PHASE.MULTI) {
    const endT = endTurn(state);
    await applyTransition(endT);
  }
  maybeStartAITurn();
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function saveGameMeta(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const meta: GameMeta = {
      mode: settings.mode,
      humanColor,
      difficulty: settings.difficulty,
    };
    localStorage.setItem(GAME_META_KEY, JSON.stringify(meta));
  } catch {
    // ignore
  }
}

function loadGameMeta(): GameMeta | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(GAME_META_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const obj = parsed as Record<string, unknown>;
    const mode: GameMode = obj.mode === 'vs_computer' ? 'vs_computer' : 'two_player';
    const humanColor: Color | null =
      obj.humanColor === BLACK || obj.humanColor === WHITE ? obj.humanColor : null;
    const difficulty = typeof obj.difficulty === 'number' ? obj.difficulty : 3;
    return { mode, humanColor, difficulty };
  } catch {
    return null;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
